import { DisciplinesType } from '@om-fe/models';
import { BemMethod, createBem } from '@om-fe/utils';
import { DateFormat } from '@om-fe/widgets/dist/components/date-format/date-format';
import { GeneralIcon } from '@om-fe/widgets/dist/components/icon/general-icon';
import React from 'react';
import { I18nContextInterface } from '@om-fe/core/core/i18n/utils';
import withTranslation from '@om-fe/core/core/i18n/with-translation';
import { DateFormatConfigKey, getConfigDateFormatType, IconSize } from '@om-fe/core/utils/config';
import { hideTimeOnHalfTime } from '../event-item/event-item.utils';
import dayjs from 'dayjs';
import isToday from 'dayjs/plugin/isToday';
import isTomorrow from 'dayjs/plugin/isTomorrow';
import getConfig from '@om-fe/core/utils/config';

dayjs.extend(isToday);
dayjs.extend(isTomorrow);

interface MatchTimeDefaultProps {
    readonly showIcon: boolean;
}

interface MatchTimePublicProps extends Partial<MatchTimeDefaultProps> {
    readonly isMatchLive: boolean;
    readonly matchTime?: number | string;
    readonly currentEventPartParam?: string;
    readonly startTime?: number;
    readonly timeLeft?: number | string;
    readonly timeLeftText?: string;
    readonly eventPartName?: string;
    readonly minuteSign?: string;
    readonly numberOfEventParts?: number;
    readonly sportId: string;
    readonly className?: string;
    readonly mapName?: string;
    readonly hasStreamingProviderId?: boolean;
    readonly showFullDate?: boolean;
}

export type MatchTimeProps = MatchTimePublicProps & MatchTimeDefaultProps & I18nContextInterface;

const bestOfExcludedSports: string[] = [
    DisciplinesType.BeachFootball,
    DisciplinesType.Basketball,
];

enum EventPartType {
    FirstHalf = '5',
    SecondHalf = '6'
}

export const bem: BemMethod = createBem('MatchTime');
const showOverTime: boolean = getConfig()?.matchListConfig?.showOverTime ?? false;

export class MatchTimeComponent extends React.PureComponent<MatchTimeProps, {}> {
    static defaultProps: MatchTimeDefaultProps = {
        showIcon: false,
    };

    infoPart(): React.ReactNode {
        const {
            startTime,
            gettext,
        } = this.props;

        const isToday: boolean = dayjs(startTime).isToday();
        const isTomorrow: boolean = dayjs(startTime).isTomorrow();
        const now: string = dayjs().format();
        const start: string = dayjs(startTime).format();
        const inMin: number = Math.ceil(dayjs(start).diff(now, 'minute', true));
        const dateElement: React.ReactNode = (
            <span className={bem('InfoPart', 'Time')}>
                <DateFormat date={startTime} pattern={getConfigDateFormatType(DateFormatConfigKey.FormatHour)} />
            </span>
        );

        if (isToday) {
            if (inMin <= 60) {
                if (inMin > 0) {
                    return (
                        <span className={bem('InfoPart', 'InMin')}>
                            {gettext('In %(inMin) min', { inMin })}
                        </span>
                    );
                } else {
                    return (
                        <span className={bem('InfoPart', 'Soon')}>
                            {gettext('Starting soon')}
                        </span>
                    );
                }
            } else {
                return (
                    <>
                        <span className={bem('InfoPart', 'Today')}>
                            {gettext('Today')}
                        </span>
                        {dateElement}
                    </>
                );
            }
        } else if (isTomorrow) {
            return (
                <>
                    <span className={bem('InfoPart', 'Tomorrow')}>
                        {gettext('Tomorrow')}
                    </span>
                    {dateElement}
                </>
            );
        } else {
            return (
                <>
                    <span className={bem('InfoPart', 'Date')}>
                        <DateFormat date={startTime} pattern={getConfigDateFormatType(DateFormatConfigKey.FormatDay)} />
                    </span>
                    {dateElement}
                </>
            );
        }
    }

    getMatchTime(): React.ReactNode {
        const {
            matchTime, eventPartName, currentEventPartParam, minuteSign, startTime,
            timeLeft, timeLeftText, isMatchLive, gettext, mapName, sportId, showFullDate,
        } = this.props;

        let displayTime: string = `${matchTime}${minuteSign}`;

        if (showOverTime && sportId === DisciplinesType.Football && currentEventPartParam === EventPartType.FirstHalf && Number(matchTime) > 45) {
            const overtime = Number(matchTime) - 45;
            displayTime = `45${minuteSign}+${overtime}`;
        } else if (showOverTime && sportId === DisciplinesType.Football && currentEventPartParam === EventPartType.SecondHalf && Number(matchTime) > 90) {
            const overtime = Number(matchTime) - 90;
            displayTime = `90${minuteSign}+${overtime}`;
        }

        if (showFullDate) {
            if (!startTime) return null;

            return (
                <>
                    <span className={bem('InfoPart', 'Date')}>
                        <DateFormat date={startTime} pattern={getConfigDateFormatType(DateFormatConfigKey.FormatDay)} />
                    </span>
                    <span className={bem('InfoPart', 'Time')}>
                        <DateFormat date={startTime} pattern={getConfigDateFormatType(DateFormatConfigKey.FormatHour)} />
                    </span>
                </>
            );
        } else if (matchTime) {
            return (
                <>
                    {matchTime && !hideTimeOnHalfTime(currentEventPartParam) && (
                        <span className={bem('InfoPart', 'MatchTime')}>
                            <span className={bem('InfoData')}>
                                {displayTime}
                            </span>
                        </span>
                    )}
                    <span className={bem('InfoPart', 'EventPartName')}>
                        {eventPartName}
                    </span>
                </>
            );
        } else if (timeLeft) {
            return (
                <>
                    <span className = {bem({
                        element: 'InfoPart',
                        modifiers: {
                            MatchTime: 'MatchTime'
                        },
                        extra: 'TimeLeftWrapper'
                    })}>
                        <span className={bem({
                            element: 'InfoData',
                            modifiers: {
                                TimeLeft: 'TimeLeft'
                            }
                        })}>
                            {timeLeftText}
                        </span>
                        <span className={bem('InfoData')}>
                            {`${timeLeft}${minuteSign}`}
                        </span>
                    </span>
                    <span className={bem('InfoPart', 'EventPartName')}>
                        {eventPartName}
                    </span>
                </>
            );
        } else if (!isMatchLive && startTime) {
            return (
                <>
                    {this.infoPart()}
                </>
            );
        } else if (eventPartName) {
            return (
                <>
                    <span className={bem('InfoPart', 'EventPartName')}>
                        {eventPartName}
                    </span>
                    {
                        mapName && (
                            <span className={bem('InfoPart', 'MapName')}>{mapName}</span>
                        )
                    }
                </>
            );
        } else if (!eventPartName && !timeLeft && isMatchLive) {
            return (
                <>
                    <span className={bem('InfoPart', 'LiveNow')}>
                        {gettext('Live now')}
                    </span>
                </>
            );
        } else {
            return null;
        }
    }

    render() {
        const {
            matchTime, timeLeft, startTime, showIcon, isMatchLive, eventPartName,
            numberOfEventParts, gettext, sportId, className, hasStreamingProviderId
        } = this.props;

        const item = this.getMatchTime();

        if (!item) {
            return null;
        }

        const isStartTime = !isMatchLive && startTime;
        const isProgress: boolean = !!(eventPartName || timeLeft || matchTime || (!eventPartName && !timeLeft && !startTime && isMatchLive));

        return (
            <span className={bem({
                modifiers: {
                    StartTime: !!isStartTime,
                    Progress: isProgress,
                },
                extra: className,
            })}>
                {showIcon && (<GeneralIcon name="clock" size={IconSize.Small1} />)}
                {numberOfEventParts && bestOfExcludedSports.indexOf(sportId) === -1 && (
                    <span className={bem('NumberOfEventPartsWrapper')}>
                        <span className={bem('NumberOfEventParts')}>
                            {gettext('Best of %(numberOfEventParts)', { numberOfEventParts })}
                        </span>
                        <span className={bem('Separator')}>&nbsp;/&nbsp;</span>
                    </span>
                )}
                {hasStreamingProviderId &&
                    <GeneralIcon
                        name="video"
                        title={gettext('Has streaming video!')}
                    />
                }
                {item}
            </span>
        );
    }
}

const MatchTime: React.ComponentType<MatchTimePublicProps> = withTranslation<MatchTimePublicProps>()
(MatchTimeComponent);
export default MatchTime;
